import { useState, useContext } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Home, FileSignature, Clock, Settings, Users, FileCheck, Building2, LogOut, Lock, AlertTriangle, BarChart3 } from 'lucide-react';
import { AppContext } from '../App';
import { useInactivityLogout } from '../hooks/useInactivityLogout';
import NotificationBell from './NotificationBell';

export const DashboardLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, membershipStatus: membership, membershipLoading: loadingMembership } = useContext(AppContext);

  // Auto-cierre de sesión por inactividad (30 minutos para clínicas)
  useInactivityLogout({
    timeoutMinutes: 30,
    logoutPath: '/login',
    tokenKey: 'konemed_token',
    additionalKeys: ['konemed_user', 'konemed_company'],
    warningMessage: 'Tu sesión ha expirado por inactividad (30 minutos)'
  });

  const handleLogout = () => {
    localStorage.removeItem('konemed_token');
    localStorage.removeItem('konemed_user');
    localStorage.removeItem('konemed_company');
    navigate('/login');
  };

  // Membership status now comes from the shared session-level fetch in App.js (see
  // useMembershipStatus in MembershipGuard.js for the same pattern). Only treat it as
  // blocked once we have a confirmed response — not while loading or after a failed fetch.
  const isBlocked = !!membership && (!membership.has_membership || membership.bloqueado);

  // Check if current user is admin (can manage users)
  const isAdmin = user?.role === 'admin';

  // Menu items with lock status
  const menuItems = [
    { path: '/dashboard', icon: Home, label: 'Dashboard', blockLevel: 'none', requiresAdmin: false },
    { path: '/nueva-firma', icon: FileSignature, label: 'Nueva Firma', blockLevel: 'full', requiresAdmin: false },
    { path: '/pendientes', icon: Clock, label: 'Pendientes', blockLevel: 'warn', requiresAdmin: false },
    { path: '/configuracion-consentimientos', icon: Settings, label: 'Consentimientos', blockLevel: 'full', requiresAdmin: false },
    { path: '/usuarios', icon: Users, label: 'Usuarios', blockLevel: 'warn', requiresAdmin: false },
    { path: '/firmados', icon: FileCheck, label: 'Firmados', blockLevel: 'warn', requiresAdmin: false },
    { path: '/datos-centro', icon: Building2, label: 'Datos Centro', blockLevel: 'none', requiresAdmin: false },
    { path: '/estadisticas', icon: BarChart3, label: 'Estadísticas', blockLevel: 'warn', requiresAdmin: false },
  ];

  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="flex h-screen bg-slate-50">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-30 w-64 bg-white border-r border-slate-200 flex flex-col transform transition-transform duration-300 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`} data-testid="sidebar">
        <div className="p-4 lg:p-6 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-600 to-indigo-600" data-testid="logo-text">
              Konemed
            </h1>
            <p className="text-xs lg:text-sm text-slate-500 mt-1">Consentimientos</p>
          </div>
          <button 
            className="lg:hidden p-2 hover:bg-slate-100 rounded-lg"
            onClick={() => setSidebarOpen(false)}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Membership Alert in Sidebar */}
        {isBlocked && (
          <div className="mx-4 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2 text-red-700">
              <AlertTriangle size={16} />
              <span className="text-xs font-semibold">Membresía Inactiva</span>
            </div>
          </div>
        )}
        
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto" data-testid="sidebar-nav">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            const isFullLocked = isBlocked && item.blockLevel === 'full';
            const isWarnLocked = isBlocked && item.blockLevel === 'warn';
            
            // Ocultar opción de Usuarios si no es admin
            if (item.requiresAdmin && !isAdmin) {
              return null;
            }
            
            if (isFullLocked) {
              return (
                <div
                  key={item.path}
                  data-testid={`nav-link-${item.label.toLowerCase().replace(/ /g, '-')}`}
                  className="flex items-center justify-between px-3 lg:px-4 py-2.5 lg:py-3 rounded-lg text-slate-400 cursor-not-allowed bg-slate-50"
                  title="Requiere membresía activa"
                >
                  <div className="flex items-center gap-3">
                    <Icon size={18} />
                    <span className="text-sm">{item.label}</span>
                  </div>
                  <Lock size={14} />
                </div>
              );
            }
            
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-link-${item.label.toLowerCase().replace(/ /g, '-')}`}
                className={`flex items-center justify-between px-3 lg:px-4 py-2.5 lg:py-3 rounded-lg transition-all ${
                  isActive
                    ? 'bg-violet-50 text-violet-600 font-semibold'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Icon size={18} />
                  <span className="text-sm">{item.label}</span>
                </div>
                {isWarnLocked && (
                  <AlertTriangle size={14} className="text-amber-500" />
                )}
              </Link>
            );
          })}
        </nav>
        
        <div className="p-4 border-t border-slate-200">
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="flex items-center gap-3 px-3 lg:px-4 py-2.5 lg:py-3 rounded-lg text-red-600 hover:bg-red-50 w-full transition-colors"
          >
            <LogOut size={18} />
            <span className="text-sm">Cerrar Sesión</span>
          </button>
        </div>
      </aside>
      
      {/* Main Content */}
      <main className="flex-1 overflow-auto" data-testid="main-content">
        {/* Mobile Header */}
        <div className="lg:hidden sticky top-0 z-10 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 hover:bg-slate-100 rounded-lg"
            data-testid="mobile-menu-button"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <h1 className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-600 to-indigo-600">
            Konemed
          </h1>
          <div data-testid="notification-bell-mobile">
            <NotificationBell />
          </div>
        </div>
        
        {/* Desktop Header with Notifications */}
        <div className="hidden lg:flex sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-3 items-center justify-end">
          <div data-testid="notification-bell-desktop">
            <NotificationBell />
          </div>
        </div>
        
        <div className="max-w-7xl mx-auto p-4 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};
